import React, { useEffect, useRef, useState } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext } from '@dnd-kit/sortable'
import { skipToken } from '@reduxjs/toolkit/query'
import { Status } from '../../../components/cards/statusTag'
import Icon from '../../../components/icon'
import Dropdown from '../../../components/inputs/dropdown'
import { SearchInput } from '../../../components/inputs/searchInput'
import { ConfirmationModal } from '../../../components/modal/app_modals/confirmationModal'
import { TableComponent } from '../../../components/table'
import { deepCopy, formatCurrency, moveElement } from '../../../helpers'
import { useDebounceSearch } from '../../../hooks/useDeboucedSearch'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useDeleteListArticleMutation,
  useEditListArticleMutation,
  useFetchAllListArticlesQuery,
  useReorderArticlesMutation,
} from '../../../redux-toolkits/lists/list.slice'
import { QueryProps } from '../../../redux-toolkits/shared/shared.type'
import { Article } from '../../../redux-toolkits/lists/list.types'

interface Props {
  className?: string
}

const ListArticlesTable: React.FC<Props> = ({
  className = '',
}): JSX.Element => {
  const [selectedArticle, setSelectedArticle] = useState<Article>()
  const [articles, setArticles] = useState<Article[]>([])
  const [query, setQuery] = useState<QueryProps>()

  const [searchParams, setSearchParams] = useSearchParams()

  const navigate = useNavigate()

  const { modal, modalOpen, handleModalClose, handleModalOpen, setModalOpen } =
    useModalControl()

  const { data, isFetching } = useFetchAllListArticlesQuery(
    query
      ? {
          query: {
            ...query,
          },
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const [deleteArticle, { isLoading: isDeleting }] =
    useDeleteListArticleMutation()

  const [editListArticle, { isLoading: isEditingArticle }] =
    useEditListArticleMutation()

  const [reorderArticles] = useReorderArticlesMutation()
  const isUserReorder = useRef(false)

  useDebounceSearch({
    searchQuery: articles,
    onSearch: async (query) => {
      if (!isUserReorder.current) return

      isUserReorder.current = false

      const reorderedArticles = query as Article[]
      if (reorderedArticles.length > 0) {
        await reorderArticles({
          articleIds: reorderedArticles.map((item) => item._id),
        })
      }
    },
    delay: 1000,
  })

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e

    if (over && active.id !== over.id) {
      const dataCopy = deepCopy(articles)
      const dataCopyMod = dataCopy.map((art) => {
        return {
          id: `${art.title}-${art._id}`,
          ...art,
        }
      })

      const oldIndex = dataCopyMod.findIndex((item) => item.id === active.id)
      const newIndex = dataCopyMod.findIndex((item) => item.id === over.id)

      const reorderedArticles = moveElement(dataCopy, oldIndex, newIndex)

      isUserReorder.current = true
      setArticles(reorderedArticles)
    }
  }

  useEffect(() => {
    if (data?.data?.result) {
      const sortedArticles = [...data.data.result]

      setArticles(sortedArticles)
    }
  }, [data])

  useEffect(() => {
    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: 20,
      search: searchParams.get('search') || undefined,
    })
  }, [searchParams])

  return (
    <>
      <SearchInput
        searchFunction={(search) => {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            search: search,
            page: '1',
          })
        }}
        placeholder="Search lists"
        className="!w-full md:!max-w-[400px]"
        initialValue={searchParams.get('search') || undefined}
      />

      <DndContext
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={
            articles.map((art) => {
              return {
                id: `${art.title}-${art._id}`,
                ...art,
              }
            }) ?? []
          }
        >
          <TableComponent
            headers={[]}
            rows={articles.map((listDetail) => {
              return {
                id: `${listDetail.title}-${listDetail._id}`,
                content: [
                  <div
                    key=""
                    className="flex gap-10 items-start !max-w-full !overflow-hidden"
                  >
                    <div className="flex flex-1 !w-full gap-3 items-start max-w-full">
                      <img
                        src={listDetail.bannerUrl}
                        className="block min-w-[140px] min-h-[140px] max-w-[140px] max-h-[140px] object-cover rounded-md aspect-square"
                        alt={`icon`}
                      />

                      <div className="max-w-full flex flex-col gap-2">
                        <div className="flex items-center flex-wrap gap-2">
                          <button
                            type="button"
                            className="w-fit text-left text-base text-black font-normal"
                            onClick={() => {
                              navigate(`/lists/articles/view/${listDetail._id}`)
                            }}
                          >
                            {listDetail.title}
                          </button>
                          ·
                          <Status
                            text={listDetail?.status?.toLowerCase() ?? ''}
                            type={
                              listDetail?.status === 'PUBLISHED'
                                ? 'success'
                                : 'warn'
                            }
                            key={`article-${listDetail._id}-status`}
                          />
                        </div>

                        <p className="text-sm text-sec-black line-clamp-3 break-words">
                          {listDetail.description}
                        </p>

                        <p className="text-sm text-black">
                          Starting from
                          <br />
                          {formatCurrency(listDetail.startingFrom)}
                        </p>
                      </div>
                    </div>

                    <Dropdown
                      key={`${listDetail._id}-controls`}
                      menuButton={
                        <Icon
                          id="ellipses"
                          height={18}
                          width={18}
                          className=""
                        />
                      }
                      closeOnClick={true}
                      menuItems={[
                        {
                          name:
                            listDetail.status === 'PUBLISHED'
                              ? 'Unpublish'
                              : 'Publish',
                          value: '',
                          hidden: false,
                          onClick: async () => {
                            await editListArticle({
                              id: listDetail._id,
                              data: {
                                article: {},
                                status:
                                  listDetail.status === 'PUBLISHED'
                                    ? 'DRAFT'
                                    : 'PUBLISHED',
                              },
                            })
                          },
                        },
                        {
                          name: 'Delete',
                          value: '',
                          className: '!text-danger',
                          hidden: false,
                          onClick: async () => {
                            setSelectedArticle(listDetail)
                            handleModalOpen('confirmArticleDeleteModal')
                          },
                        },
                      ]}
                      loading={isDeleting || isEditingArticle}
                    />
                  </div>,
                ],
              }
            })}
            name="lists-table"
            isRowClickable={false}
            loading={isFetching}
            isEmpty={[undefined, 0].includes(articles.length)}
            currentPage={Number(query?.page ?? 1)}
            totalDataCount={data?.data?.count}
            pageLimit={Number(query?.limit ?? 0)}
            pageRangeDisplayed={3}
            onPageChange={(pageNo) => {
              setQuery({ ...query, page: pageNo })
            }}
            sortable
          />
        </SortableContext>
      </DndContext>

      <ConfirmationModal
        title="Delete Good List?"
        open={modal === 'confirmArticleDeleteModal' && modalOpen}
        setOpen={setModalOpen}
        message={
          <div>
            Are you sure you want to delete the{' '}
            <span className="">&quot;{selectedArticle?.title}</span>
            &quot; good list?
          </div>
        }
        onCloseCallback={() => {
          setSelectedArticle(undefined)
        }}
        loading={isDeleting}
        onConfirm={() => {
          if (selectedArticle) {
            deleteArticle({ id: selectedArticle?._id }).then((resp) => {
              if (resp.data?.success) {
                setSelectedArticle(undefined)
                handleModalClose()
              }
            })
          }
        }}
      />
    </>
  )
}

export default ListArticlesTable
